/**
 * Funding Sources management tab.
 *
 * Admin / super_admin can CRUD funding sources and tag each with the billing
 * party that invoices under that funding source should be addressed to.
 *
 * Billing-party resolution rules (enforced server-side by
 * services/billing_resolver.py):
 *   • billing_mode = "fixed_company"   → invoice bill-to = this entity
 *   • billing_mode = "session_company" → invoice bill-to = session's own
 *                                        client company (self-pay)
 *
 * Entities are either (a) linked to an existing Companies record for full
 * address / SST / bank snapshotting, or (b) inlined on the funding source
 * document itself (billing_company_name + billing_address + billing_registration_no)
 * for quick setup without maintaining a separate Companies entry.
 */
import React, { useEffect, useState } from "react";
import { axiosInstance } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, RefreshCcw, Database } from "lucide-react";

const BADGE_COLORS = [
  { v: "blue", label: "Blue" },
  { v: "emerald", label: "Green" },
  { v: "amber", label: "Yellow" },
  { v: "red", label: "Red" },
  { v: "purple", label: "Purple" },
  { v: "pink", label: "Pink" },
  { v: "slate", label: "Grey" },
];

const BILLING_MODES = [
  { v: "fixed_company", label: "Fixed Company — bills to the entity below" },
  { v: "session_company", label: "Session Company — bills to the client that bought the training" },
];

const empty = {
  code: "",
  label: "",
  badge_color: "amber",
  display_order: 100,
  billing_mode: "fixed_company",
  billing_company_id: "",
  billing_company_name: "",
  billing_address: "",
  billing_registration_no: "",
};

export const FundingSourcesTab = () => {
  const [sources, setSources] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | <source object>
  const [form, setForm] = useState({ ...empty });
  const [saving, setSaving] = useState(false);
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillPreview, setBackfillPreview] = useState(null);
  const [marketingOpen, setMarketingOpen] = useState(false);
  const [marketingLoading, setMarketingLoading] = useState(false);
  const [marketingPreview, setMarketingPreview] = useState(null);
  const [onlyNegative, setOnlyNegative] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const [srcRes, coRes] = await Promise.all([
        axiosInstance.get("/funding-sources?include_inactive=true"),
        axiosInstance.get("/companies"),
      ]);
      setSources(srcRes.data || []);
      setCompanies(coRes.data || []);
    } catch (e) {
      toast.error("Failed to load funding sources");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm({ ...empty });
    setEditing("new");
  };

  const openEdit = (src) => {
    setForm({
      code: src.code,
      label: src.label,
      badge_color: src.badge_color || "amber",
      display_order: src.display_order ?? 100,
      billing_mode: src.billing_mode || "fixed_company",
      billing_company_id: src.billing_company_id || "",
      billing_company_name: src.billing_company_name || "",
      billing_address: src.billing_address || "",
      billing_registration_no: src.billing_registration_no || "",
    });
    setEditing(src);
  };

  const close = () => {
    setEditing(null);
    setForm({ ...empty });
  };

  const save = async () => {
    if (!form.label.trim() || !form.code.trim()) {
      toast.error("Code and Label are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        label: form.label.trim(),
        badge_color: form.badge_color,
        display_order: Number(form.display_order) || 100,
        billing_mode: form.billing_mode,
        billing_company_id: form.billing_company_id || null,
        billing_company_name: form.billing_company_name || null,
        billing_address: form.billing_address || null,
        billing_registration_no: form.billing_registration_no || null,
      };
      if (editing === "new") {
        await axiosInstance.post("/funding-sources", {
          code: form.code.trim().toLowerCase().replace(/\s+/g, "_"),
          ...payload,
        });
        toast.success("Funding source added");
      } else {
        await axiosInstance.put(`/funding-sources/${editing.id}`, payload);
        toast.success("Funding source updated");
      }
      await load();
      close();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (src) => {
    try {
      if (src.is_active) {
        await axiosInstance.delete(`/funding-sources/${src.id}`);
        toast.success("Deactivated");
      } else {
        await axiosInstance.put(`/funding-sources/${src.id}`, { is_active: true });
        toast.success("Reactivated");
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const runBackfill = async (commit) => {
    setBackfillLoading(true);
    try {
      const res = await axiosInstance.post(
        `/superadmin/finance/billing-party-backfill?dry_run=${commit ? "false" : "true"}`
      );
      setBackfillPreview(res.data);
      if (commit) {
        toast.success(`Backfill done — ${res.data.changed_count} invoice(s) updated`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfillLoading(false);
    }
  };

  const runMarketingRecompute = async (commit) => {
    setMarketingLoading(true);
    try {
      const res = await axiosInstance.post(
        `/superadmin/finance/marketing-commission-recompute?dry_run=${commit ? "false" : "true"}&only_negative=${onlyNegative ? "true" : "false"}`
      );
      setMarketingPreview(res.data);
      if (commit) {
        toast.success(`Marketing recompute done — ${res.data.changed_count} row(s) fixed`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Recompute failed");
    } finally {
      setMarketingLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="funding-sources-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Funding Sources</h3>
          <p className="text-sm text-gray-500">
            Controls the funding badge on invoices AND the bill-to party they
            are addressed to. Add each payor's full address / registration
            number so invoice PDFs print correctly.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => { setBackfillOpen(true); setBackfillPreview(null); }}
            data-testid="billing-backfill-open-btn"
          >
            <Database className="w-4 h-4 mr-2" />
            Backfill Billing Party
          </Button>
          <Button
            variant="outline"
            onClick={() => { setMarketingOpen(true); setMarketingPreview(null); }}
            className="border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800"
            data-testid="marketing-recompute-open-btn"
          >
            <RefreshCcw className="w-4 h-4 mr-2" />
            Recompute Marketing Commissions
          </Button>
          <Button onClick={openNew} data-testid="add-funding-source-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Funding Source
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <div className="overflow-x-auto border rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left">Code</th>
                <th className="px-3 py-2 text-left">Label</th>
                <th className="px-3 py-2 text-left">Badge</th>
                <th className="px-3 py-2 text-left">Billing Mode</th>
                <th className="px-3 py-2 text-left">Billed To</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sources.map((s) => (
                <tr
                  key={s.id}
                  className="border-t"
                  data-testid={`funding-source-row-${s.code}`}
                >
                  <td className="px-3 py-2 font-mono text-xs">{s.code}</td>
                  <td className="px-3 py-2">{s.label}</td>
                  <td className="px-3 py-2">
                    <Badge className={`bg-${s.badge_color}-100 text-${s.badge_color}-700`}>
                      {s.badge_color}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {s.billing_mode === "session_company" ? "Session company" : "Fixed company"}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {s.billing_mode === "session_company"
                      ? "—"
                      : s.billing_company_name || <span className="text-red-500">⚠ not set</span>}
                  </td>
                  <td className="px-3 py-2">
                    {s.is_active ? (
                      <Badge className="bg-green-100 text-green-700">Active</Badge>
                    ) : (
                      <Badge className="bg-gray-200 text-gray-600">Inactive</Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openEdit(s)}
                      data-testid={`edit-funding-source-${s.code}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => toggleActive(s)}
                      data-testid={`toggle-funding-source-${s.code}`}
                    >
                      {s.is_active ? (
                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                      ) : (
                        <RefreshCcw className="w-3.5 h-3.5 text-green-600" />
                      )}
                    </Button>
                  </td>
                </tr>
              ))}
              {sources.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-4 text-center text-gray-500">
                    No funding sources yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && close()}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing === "new" ? "Add Funding Source" : "Edit Funding Source"}
            </DialogTitle>
            <DialogDescription>
              The billing details below are copied onto every invoice created
              under this funding source. Keep them accurate — they print on
              the invoice PDF.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Code *</Label>
                <Input
                  value={form.code}
                  disabled={editing !== "new"}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  placeholder="e.g. hrdcorp"
                  data-testid="funding-form-code"
                />
              </div>
              <div>
                <Label>Display Label *</Label>
                <Input
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  placeholder="HRDCorp Grant"
                  data-testid="funding-form-label"
                />
              </div>
              <div>
                <Label>Badge Color</Label>
                <Select
                  value={form.badge_color}
                  onValueChange={(v) => setForm({ ...form, badge_color: v })}
                >
                  <SelectTrigger data-testid="funding-form-badge-color">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BADGE_COLORS.map((c) => (
                      <SelectItem key={c.v} value={c.v}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Display Order</Label>
                <Input
                  type="number"
                  value={form.display_order}
                  onChange={(e) =>
                    setForm({ ...form, display_order: e.target.value })
                  }
                />
              </div>
            </div>

            <div>
              <Label>Billing Mode</Label>
              <Select
                value={form.billing_mode}
                onValueChange={(v) => setForm({ ...form, billing_mode: v })}
              >
                <SelectTrigger data-testid="funding-form-billing-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BILLING_MODES.map((m) => (
                    <SelectItem key={m.v} value={m.v}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {form.billing_mode === "fixed_company" && (
              <div className="border rounded-lg p-3 bg-gray-50 space-y-3">
                <p className="text-xs text-gray-600">
                  Choose an existing company below (preferred — picks up its
                  full address / SST / bank info automatically), OR fill in
                  the inline fields underneath.
                </p>
                <div>
                  <Label>Link to Existing Company</Label>
                  <Select
                    value={form.billing_company_id || "none"}
                    onValueChange={(v) =>
                      setForm({
                        ...form,
                        billing_company_id: v === "none" ? "" : v,
                        billing_company_name:
                          v === "none"
                            ? form.billing_company_name
                            : companies.find((c) => c.id === v)?.name || "",
                      })
                    }
                  >
                    <SelectTrigger data-testid="funding-form-company-picker">
                      <SelectValue placeholder="— no linked company —" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— no linked company —</SelectItem>
                      {companies.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label>Billing Company Name</Label>
                    <Input
                      value={form.billing_company_name}
                      onChange={(e) =>
                        setForm({ ...form, billing_company_name: e.target.value })
                      }
                      placeholder="HRDCorp Berhad"
                      data-testid="funding-form-company-name"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Billing Address</Label>
                    <Textarea
                      rows={2}
                      value={form.billing_address}
                      onChange={(e) =>
                        setForm({ ...form, billing_address: e.target.value })
                      }
                      placeholder="Wisma HRDCorp, Jalan Beringin, 50480 Kuala Lumpur"
                      data-testid="funding-form-address"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Registration No / Company ID</Label>
                    <Input
                      value={form.billing_registration_no}
                      onChange={(e) =>
                        setForm({ ...form, billing_registration_no: e.target.value })
                      }
                      placeholder="e.g. 201401035414 (1113761-A)"
                      data-testid="funding-form-reg-no"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={close} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="funding-form-save">
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Backfill dialog */}
      <Dialog open={backfillOpen} onOpenChange={setBackfillOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Backfill Billing Party on Existing Invoices</DialogTitle>
            <DialogDescription>
              Re-applies each funding source's current billing config to all
              pre-issue invoices (draft / auto_draft / finance_review /
              approved). Additional invoices and locked invoices are skipped.
              Run <strong>Dry Run</strong> first to see what would change.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => runBackfill(false)}
                disabled={backfillLoading}
                data-testid="backfill-dry-run-btn"
              >
                {backfillLoading && !backfillPreview?.dry_run === false
                  ? "Scanning…"
                  : "Dry Run (preview)"}
              </Button>
              <Button
                onClick={() => runBackfill(true)}
                disabled={
                  backfillLoading ||
                  !backfillPreview ||
                  !backfillPreview.dry_run ||
                  (backfillPreview.changed_count || 0) === 0
                }
                className="bg-red-600 hover:bg-red-700"
                data-testid="backfill-commit-btn"
              >
                Apply Backfill ({backfillPreview?.changed_count || 0})
              </Button>
            </div>

            {backfillPreview && (
              <div className="border rounded-lg p-3 text-xs space-y-2 bg-gray-50 max-h-[50vh] overflow-y-auto">
                <div>
                  <strong>Scanned:</strong> {backfillPreview.total_scanned} invoices ·{" "}
                  <strong>Would change:</strong> {backfillPreview.changed_count} ·{" "}
                  <strong>Skipped:</strong> {backfillPreview.skipped_count}
                </div>
                {(backfillPreview.changes || []).map((c, i) => (
                  <div key={i} className="border-l-2 border-blue-400 pl-2 py-1">
                    <div className="font-mono text-[11px]">
                      {c.invoice_number} <span className="text-gray-500">({c.funding_source || "no-source"})</span>
                    </div>
                    <div className="text-gray-600">
                      <span className="text-red-600 line-through">
                        {c.from?.bill_to_name || c.from?.company_name || "—"}
                      </span>
                      {" → "}
                      <span className="text-green-700 font-semibold">
                        {c.to?.bill_to_name || c.to?.company_name || "—"}
                      </span>
                    </div>
                  </div>
                ))}
                {(backfillPreview.changes || []).length === 0 && (
                  <div className="text-gray-500">
                    Nothing to change — all pre-issue invoices already match
                    their funding source's billing config.
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {/* Marketing recompute dialog */}
      <Dialog open={marketingOpen} onOpenChange={setMarketingOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Recompute Marketing Commissions</DialogTitle>
            <DialogDescription>
              Rebuilds `marketing_commissions.calculated_amount` from live
              session data. Fixes stale / negative commissions that happened
              when marketing was saved before the invoice existed (e.g.
              Chandra −RM358.40 on Antah Schindler, Malek −RM101.25 on
              Pertubuhan Peladang). Negative results are clamped to RM0.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={onlyNegative}
                onChange={(e) => setOnlyNegative(e.target.checked)}
                data-testid="marketing-only-negative"
              />
              Scope to rows with negative stored amount only
            </label>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => runMarketingRecompute(false)}
                disabled={marketingLoading}
                data-testid="marketing-dry-run-btn"
              >
                {marketingLoading ? "Scanning…" : "Dry Run (preview)"}
              </Button>
              <Button
                onClick={() => runMarketingRecompute(true)}
                disabled={
                  marketingLoading ||
                  !marketingPreview ||
                  !marketingPreview.dry_run ||
                  (marketingPreview.changed_count || 0) === 0
                }
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="marketing-commit-btn"
              >
                Apply Recompute ({marketingPreview?.changed_count || 0})
              </Button>
            </div>

            {marketingPreview && (
              <div className="border rounded-lg p-3 text-xs space-y-2 bg-gray-50 max-h-[50vh] overflow-y-auto">
                <div>
                  <strong>Scanned:</strong> {marketingPreview.scanned} ·{" "}
                  <strong>Would change:</strong> {marketingPreview.changed_count}
                </div>
                {(marketingPreview.changes || []).map((c, i) => (
                  <div key={i} className="border-l-2 border-amber-400 pl-2 py-1">
                    <div className="font-mono text-[11px]">
                      {c.marketing_user_name} —{" "}
                      <span className="text-gray-500">{c.company_name}</span>{" "}
                      ({c.commission_rate}%)
                    </div>
                    <div className="text-gray-600">
                      <span className="text-red-600 line-through">
                        RM {Number(c.old_amount).toFixed(2)}
                      </span>
                      {" → "}
                      <span className="text-green-700 font-semibold">
                        RM {Number(c.new_amount).toFixed(2)}
                      </span>
                    </div>
                  </div>
                ))}
                {(marketingPreview.changes || []).length === 0 && (
                  <div className="text-gray-500">
                    Nothing to recompute — all marketing commissions already
                    match their live session data.
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FundingSourcesTab;
